import React, { useState } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Typography,
  CircularProgress,
  InputLabel,
  LinearProgress,
  Paper,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import CustomSnackbar from "../Utils/CustomSnackbar";
import { resetPaswword } from "../api/action";
import Logo from "../Image/Logo.png";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import HttpsIcon from '@mui/icons-material/Https';

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const passwordRegex =
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

  const getLastTwoParts = (url) => {
    const parts = url.replace(/\/$/, "").split("/");
    return parts.slice(-2).join("/");
  };
  const resetToken = getLastTwoParts(location.pathname);

  const passwordRules = [
    { text: "At least 8 characters long", test: (p) => p.length >= 8 },
    { text: "One uppercase letter (A–Z)", test: (p) => /[A-Z]/.test(p) },
    { text: "One lowercase letter (a–z)", test: (p) => /[a-z]/.test(p) },
    { text: "One number (0–9)", test: (p) => /\d/.test(p) },
    {
      text: "One special character (@$!%*?&)",
      test: (p) => /[@$!%*?&]/.test(p),
    },
  ];

  const getStrength = () => {
    let score = passwordRules.reduce(
      (acc, rule) => acc + (rule.test(password) ? 1 : 0),
      0
    );
    if (score <= 2) return { label: "Weak", color: "red", value: 30 };
    if (score === 3 || score === 4)
      return { label: "Medium", color: "orange", value: 70 };
    if (score === 5) return { label: "Strong", color: "green", value: 100 };
    return { label: "", color: "", value: 0 };
  };
  const strength = getStrength();

  const handleReset = (e) => {
    e.preventDefault();
    if (!password || !confirmPass) {
      showSnackbar("Please fill all the fields.", "error");
      return;
    }
    if (!passwordRegex.test(password)) {
      showSnackbar("Password must meet all policy requirements.", "error");
      return;
    }
    if (password !== confirmPass) {
      showSnackbar("Passwords do not match.", "error");
      return;
    }
    setLoading(true);
    dispatch(
      resetPaswword(
        { password, password2: confirmPass },
        resetToken,
        (data) => {
          setLoading(false);
          if (data?.data?.msg === "Password Reset Successfully") {
            showSnackbar(data?.data?.msg, "success");
            setTimeout(() => navigate("/"), 2000);
          } else {
            showSnackbar(data?.data?.msg, "error");
          }
        },
        (error) => {
          setLoading(false);
          const msg =
            error.message === "Network Error"
              ? error.message
              : error.response?.data?.detail ||
                "Session expired. Please log in again.";
          showSnackbar(msg, "error");
        }
      )
    );
  };

  const showSnackbar = (msg, severity) => {
    setSnackbarMessage(msg);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  return (
    <Grid container sx={{ height: "100vh" }}>
      {/* Left Section */}
      <Grid
        item
        xs={false}
        md={6}
        sx={{
          position: "relative",
          // backgroundImage: `url(${Logo})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        {/* Gradient Overlay */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background:
            "rgba(29, 57, 105, 1)",
            zIndex: 1,
          }}
        />

        {/* Content */}
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            color: "#fff",
            maxWidth: 400,
          }}
        >
          <img
            src={Logo}
            alt="Healthcare Logo"
            style={{
              width: "100%",
              height: "100%",
              background: "#fff",
              padding: "8px",
              borderRadius: "12px",
            }}
          />
          {/* <Typography variant="h4" fontWeight="bold" gutterBottom>
            Welcome to Precise Behavioral Health
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Your trusted partner in managing health records, billing, and care
            efficiently. Sign in and access your personalized dashboard today.
          </Typography> */}
        </Box>
      </Grid>

      {/* Right Section */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Paper elevation={3} sx={{ width: 400, p: 4, borderRadius: "12px" }}>
          <Box textAlign="center" mb={3}>
            <HttpsIcon sx={{ fontSize: 50, color: "rgba(29, 57, 105, 1)" }} />
            <Typography variant="h6" fontWeight="bold">
              Reset Your Password
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Reset your password and regain access instantly.
            </Typography>
          </Box>

          {/* New Password */}
          <InputLabel sx={{ mt: 2 }}>New Password</InputLabel>
          <TextField
            fullWidth
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter new password"
            sx={{ mt: 1 }}
          />

          {/* Strength Meter */}
          {password && (
            <Box sx={{ mt: 1 }}>
              <LinearProgress
                variant="determinate"
                value={strength.value}
                sx={{
                  height: 8,
                  borderRadius: 5,
                  background: "#ddd",
                  "& .MuiLinearProgress-bar": {
                    backgroundColor: strength.color,
                  },
                }}
              />
              <Typography
                variant="caption"
                sx={{
                  color: strength.color,
                  fontWeight: "bold",
                  mt: 0.5,
                  display: "block",
                }}
              >
                {strength.label} Password
              </Typography>
            </Box>
          )}

          {/* Confirm Password */}
          <InputLabel sx={{ mt: 2 }}>Confirm Password</InputLabel>
          <TextField
            fullWidth
            type="password"
            value={confirmPass}
            onChange={(e) => setConfirmPass(e.target.value)}
            placeholder="Re-enter password"
            sx={{ mt: 1 }}
          />

          {/* Rules */}
          <Box sx={{ mt: 2 }}>
            {passwordRules.map((rule, idx) => (
              <Typography
                key={idx}
                variant="body2"
                sx={{
                  color: rule.test(password) ? "green" : "gray",
                  fontSize: "13px",
                  mb: 0.5,
                }}
              >
                {rule.test(password) ? "✔" : "✖"} {rule.text}
              </Typography>
            ))}
          </Box>

          {/* Submit Button */}
          <Button
            fullWidth
            variant="contained"
            onClick={handleReset}
            disableElevation
            sx={{
              mt: 3,
              py: 1.2,
              background:
              "rgba(29, 57, 105, 1)",
              fontWeight: "bold",
              textTransform: "none",
              borderRadius: "8px",
              // "&:hover": { background: "#009578" },
              color: "#fff",
            }}
          >
            {loading ? (
              <CircularProgress size={20} sx={{ color: "#fff" }} />
            ) : (
              "Reset Password"
            )}
          </Button>

          {/* Back to Login */}
          <Typography
            onClick={() => navigate("/")}
            sx={{
              color: "rgba(29, 57, 105, 1)",
              textAlign: "center",
              cursor: "pointer",
              fontSize: "13px",
              mt: 2,
            }}
          >
            ⬅ Back to Login
          </Typography>
        </Paper>
      </Grid>

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />
    </Grid>
  );
};

export default ResetPassword;
